import React, { lazy, Suspense } from "react";
import { Modal } from "react-bootstrap";
import { BASE_URL, URL } from "../../../redux/constants";
import PanZoomAsset from "./PanZoomAsset";

const ProductModelViewer = lazy(() => import("./ProductModelViewer"));

/**
 * 根据资源类型渲染卡片标题图标。
 * @param {{ type: "pinout"|"footprint"|"model" }} props 组件属性
 * @returns {React.JSX.Element} 资源类型图标
 */
const AssetIcon = ({ type }) => {
  if (type === "model") {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
        <path d="m4.5 7.8 7.5 4.3 7.5-4.3M12 12.1V21" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
      <path d="M9 3.5v3M15 3.5v3M9 17.5v3M15 17.5v3M3.5 9h3M3.5 15h3M17.5 9h3M17.5 15h3" />
      {type === "pinout" && <path d="M9.5 9.5h5v5h-5z" />}
    </svg>
  );
};

/**
 * 渲染单个工程资源卡片的统一标题和内容容器。
 * @param {{ type: "pinout"|"footprint"|"model", title: string, eyebrow: string, description: string, format: string, children: React.ReactNode }} props 组件属性
 * @returns {React.JSX.Element} 工程资源卡片
 */
const AssetCard = ({ type, title, eyebrow, description, format, children }) => (
  <section className="min-w-0 overflow-hidden rounded-2xl border border-[#dfe7f2] bg-white shadow-[0_8px_28px_rgba(31,55,88,0.06)]">
    <header className="flex min-h-[94px] items-center justify-between gap-3 border-b border-[#edf1f6] px-4 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
          type === "pinout"
            ? "bg-[#eaf2ff] text-[#4a83f6]"
            : type === "footprint"
              ? "bg-[#f3eeff] text-[#8d64df]"
              : "bg-[#e6f8f4] text-[#29aa8f]"
        }`}>
          <AssetIcon type={type} />
        </span>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <h3 className="mb-0 text-base font-bold tracking-tight text-[#27344a]">{title}</h3>
            <span className="hidden text-[10px] font-bold uppercase tracking-[0.1em] text-[#b0bac9] xl:inline">
              {eyebrow}
            </span>
          </div>
          <p className="mb-0 mt-1 truncate text-[11px] text-[#96a2b4]">{description}</p>
        </div>
      </div>
      <span className={`shrink-0 rounded-md border px-2 py-1 text-[10px] font-semibold ${
        type === "model"
          ? "border-[#d5ece5] bg-[#f2fbf8] text-[#4d8d7c]"
          : "border-[#e4e9f0] bg-[#fafbfd] text-[#929eaf]"
      }`}>
        {format}
      </span>
    </header>
    {children}
  </section>
);

/**
 * 生成通过后端 API 读取 STEP/STP 的预览地址，避免静态文件跨域限制。
 * @param {Record<string, any>|undefined} product 商品数据
 * @returns {string|undefined} STEP/STP 预览 API 地址
 */
const buildModelPreviewUrl = (product) => {
  if (!product?._id || !product?.model3dSource) return undefined;
  const backendUrl = (BASE_URL || "").replace(/\/$/, "");
  return `${backendUrl}${URL}/products/${encodeURIComponent(product._id)}/model3d-source`;
};

/**
 * 生成按商品 ID 读取最新工程图片的 API 地址，避免使用替换前的文件 URL。
 * @param {Record<string, any>|undefined} product 商品数据
 * @param {"pinout"|"footprint"} type 工程图片类型
 * @returns {string|undefined} 工程图片预览 API 地址
 */
const buildEngineeringImagePreviewUrl = (product, type) => {
  const productField = type === "pinout" ? "pinoutImage" : "footprintImage";
  if (!product?._id || !product?.[productField]) return undefined;
  const backendUrl = (BASE_URL || "").replace(/\/$/, "");
  return `${backendUrl}${URL}/products/${encodeURIComponent(product._id)}/engineering-image/${type}`;
};

/**
 * 展示商品 PDF、引脚图、焊盘图和三维模型的数据手册弹窗。
 * @param {{ show: boolean, onHide: () => void, product?: Record<string, any> }} props 组件属性
 * @returns {React.JSX.Element} 数据手册弹窗
 */
const EngineeringAssetsDialog = ({ show, onHide, product }) => {
  const pinoutPreviewSource = buildEngineeringImagePreviewUrl(product, "pinout");
  const footprintPreviewSource = buildEngineeringImagePreviewUrl(product, "footprint");
  const modelPreviewSource = buildModelPreviewUrl(product);
  const hasEngineeringAssets = Boolean(
    pinoutPreviewSource || footprintPreviewSource || modelPreviewSource,
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      dialogClassName="!w-[calc(100%_-_2rem)] !max-w-[1560px]"
      aria-labelledby="engineering-assets-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="engineering-assets-title" className="text-lg font-bold">
          {product?.CTYEA_Part_Number || product?.Manufacturer_Part_Number || "Product"} Datasheet
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-[#f5f8fc] p-4 md:p-5">
        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-[#e4eaf3] bg-white p-4 shadow-[0_5px_20px_rgba(35,55,90,0.04)] md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <p className="mb-1 text-base font-bold tracking-tight text-[#27344a]">
              {product?.Manufacturer_Part_Number || product?.name}
            </p>
            <p className="mb-0 text-xs text-[#8793a6]">PDF, pinout, footprint, and interactive 3D engineering resources</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {product?.datasheet && (
              <a href={product.datasheet} target="_blank" rel="noreferrer" className="rounded-lg border border-[#dce5f2] bg-white px-4 py-2 text-sm font-semibold text-[#3778e8] no-underline shadow-sm transition hover:border-blue-300 hover:bg-blue-50">
                View PDF
              </a>
            )}
          </div>
        </div>

        {hasEngineeringAssets ? (
          <>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <AssetCard
                type="pinout"
                title="Pinout Diagram"
                eyebrow="PINOUT"
                description="Pin numbers and signal definitions"
                format="SVG / PNG"
              >
                <PanZoomAsset src={pinoutPreviewSource} alt={`${product?.name || "Product"} pinout`} label="PIN DIAGRAM" />
              </AssetCard>
              <AssetCard
                type="footprint"
                title="PCB Footprint"
                eyebrow="FOOTPRINT"
                description="Package and pad layout"
                format="SVG / PNG"
              >
                <PanZoomAsset src={footprintPreviewSource} alt={`${product?.name || "Product"} footprint`} label="PCB FOOTPRINT" dark />
              </AssetCard>
              <AssetCard
                type="model"
                title="3D Model"
                eyebrow="3D MODEL"
                description="Interactive package preview"
                format="STEP"
              >
                {modelPreviewSource ? (
                  <Suspense fallback={<div className="flex h-[360px] items-center justify-center text-sm text-gray-500">Loading 3D viewer…</div>}>
                    <ProductModelViewer src={modelPreviewSource} format="step" />
                  </Suspense>
                ) : (
                  <div className="flex h-[360px] items-center justify-center bg-gradient-to-br from-[#eef3fa] to-[#dfe8f5] px-6 text-center text-sm text-gray-500">
                    No 3D preview model available
                  </div>
                )}
              </AssetCard>
            </div>
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#dfe9f8] bg-[#edf4fd] px-4 py-3 text-[11px] leading-5 text-[#70809a]">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#d7e8ff] font-bold italic text-[#3978e2]">i</span>
              <p className="mb-0">
                <strong className="text-[#486b9e]">Preview controls:</strong> scroll to zoom images, drag to pan, rotate the 3D model with the left mouse button, and use Shift + drag to pan it.
              </p>
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
            No pinout, footprint, or 3D preview model is available for this product.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EngineeringAssetsDialog;
